/**
 * Short-code format constants and pure helpers (short-card-code feature).
 *
 * Single source of truth for the human-friendly `shortCode` assigned to every
 * new `Card_Record`. Every call site (Device_Ingest_Lambda, Intake_Confirm_Path,
 * Catalog_Service, Catalog_UI) consumes the alphabet, length, retry budget,
 * generator, and normalizer from here rather than redefining them locally
 * (Req 1.5, 4.2, 5.2).
 *
 * Everything in this module is pure and runtime-agnostic: no AWS SDK, no
 * Node-only or browser-only API.
 */

/**
 * Crockford-style alphabet: the 26 uppercase letters A–Z and the 10 digits 0–9
 * with the five visually-confusable characters `O`, `0`, `I`, `1`, `L`
 * excluded. Exactly 31 distinct characters, no lowercase, no duplicates
 * (Req 1.1).
 */
export const SHORT_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

/** Number of characters in a short code (Req 1.2). */
export const SHORT_CODE_LENGTH = 6;

/**
 * Bounded generate-and-conditional-insert attempt budget, an integer in
 * [1, 100] (Req 1.3).
 */
export const SHORT_CODE_MAX_ATTEMPTS = 10;

/**
 * Confusable input fold applied AFTER uppercasing (Crockford-style). Because
 * `0` and `1` are themselves excluded from `SHORT_CODE_ALPHABET`, any folded
 * character is out-of-alphabet and a code containing it is rejected as
 * malformed by `isCanonicalShortCode` (Req 7.3). Case-folding is therefore the
 * normalization that actually resolves a typed code to a stored one.
 */
export const SHORT_CODE_CONFUSABLE_MAP: Readonly<Record<string, string>> = {
  O: '0',
  I: '1',
  L: '1',
};

/**
 * Produce a candidate short code: a string of exactly `SHORT_CODE_LENGTH`
 * characters, each drawn from `SHORT_CODE_ALPHABET` (Req 1.4). Every candidate
 * is in Canonical_Form by construction.
 *
 * The `rng` seam (defaulting to `Math.random`) keeps generation deterministic
 * under test. `rng` is expected to return a float in [0, 1).
 */
export function generateShortCodeCandidate(rng: () => number = Math.random): string {
  let code = '';
  for (let i = 0; i < SHORT_CODE_LENGTH; i += 1) {
    const index = Math.floor(rng() * SHORT_CODE_ALPHABET.length);
    code += SHORT_CODE_ALPHABET.charAt(index);
  }
  return code;
}

/**
 * Normalize an arbitrary input string to Canonical_Form (Req 7.2, 8.1, 8.4):
 * trim leading/trailing whitespace → uppercase → fold confusables per
 * `SHORT_CODE_CONFUSABLE_MAP` (O→0, I→1, L→1). Pure and total over any string.
 */
export function normalizeShortCode(input: string): string {
  const uppercased = input.trim().toUpperCase();
  let normalized = '';
  for (const char of uppercased) {
    normalized += SHORT_CODE_CONFUSABLE_MAP[char] ?? char;
  }
  return normalized;
}

/**
 * True iff `s` is exactly `SHORT_CODE_LENGTH` characters, each a member of
 * `SHORT_CODE_ALPHABET` (Req 7.3).
 */
export function isCanonicalShortCode(s: string): boolean {
  if (s.length !== SHORT_CODE_LENGTH) return false;
  for (const char of s) {
    if (!SHORT_CODE_ALPHABET.includes(char)) return false;
  }
  return true;
}
